// LAYOUT-SPEC §0 — a SOW's version of a deliverable (D105)
//
//   ONE QUESTION  How does this SOW carry this deliverable out: performed
//                 here, routed to another team, or not owed — and on what
//                 terms and schedule?
//   PRIMARY OBJECT  One duty assignment (Archetype C).
//   PRIMARY VERB    Save (SetDutyAssignment writes the whole set, team rules
//                 included, and mints in the same transaction).
//
// Opened by ?assign=<teamId>:<sowId>:<dutyId> (or …:new to handle another
// award deliverable). The existing assignment is read from GetDuty; the form's
// fields are seeded once, at mount, from that read — never re-synced.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraSectionHeader, SpecraSegmentedControl, SpecraSelect, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { useCmDrawerParams } from '../drawerParams';
import { useDuty, useSetDutyAssignment, useWorkstream } from '../hooks';
import { mintReceipt } from '../lib/rule';
import { NOT_OWED_REASON_LABEL } from '../lib/status';
import { useCmLookups } from '../lookups';
import { CmDrawer } from './CmDrawer';
import { draftFromRule, draftProblem, fieldsFromDraft, RuleForm } from './RuleEditor';
const OVERRIDES = [
    {
        field: 'title_as_stated',
        key: 'titleAsStated',
        label: 'Title as the SOW states it'
    },
    {
        field: 'recipient',
        key: 'recipient',
        label: 'Recipient'
    },
    {
        field: 'format',
        key: 'format',
        label: 'Format'
    },
    {
        field: 'submission_method',
        key: 'submissionMethod',
        label: 'How it is sent'
    },
    {
        field: 'acceptance_criteria',
        key: 'acceptanceCriteria',
        label: 'Acceptance'
    }
];
function seedOverrides(a) {
    const out = {};
    for (const o of OVERRIDES){
        const loc = a?.overrideLocators.find((l)=>l.field === o.field);
        out[o.key] = {
            value: a?.[o.key] ?? '',
            locator: loc?.sourceLocator ?? '',
            quote: loc?.quote ?? ''
        };
    }
    return out;
}
export function AssignmentDrawer({ programId, target }) {
    const [teamId, sowId, dutyParam] = target.split(':');
    const isNew = dutyParam === 'new' || !dutyParam;
    const duty = useDuty(programId, isNew ? undefined : dutyParam);
    const params = useCmDrawerParams();
    // Mount the form only once the existing assignment has been read, so its
    // seed is the stored row (a keyed remount, never a sync effect).
    if (!isNew && duty.isLoading) {
        return /*#__PURE__*/ _jsx(CmDrawer, {
            title: "Handling",
            onClose: ()=>params.close('assign'),
            testId: Testids.cmAssignment.drawer,
            children: /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                children: "Loading…"
            })
        });
    }
    // A failed read must not mount a blank form: SetDutyAssignment writes the
    // whole set, so saving it would wipe the stored overrides and team rules.
    if (!isNew && duty.error) {
        return /*#__PURE__*/ _jsx(CmDrawer, {
            title: "Handling",
            onClose: ()=>params.close('assign'),
            testId: Testids.cmAssignment.drawer,
            children: /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "caption",
                tone: "danger",
                role: "alert",
                children: apiErrorMessage(duty.error, 'Could not load this deliverable. Close and try again.')
            })
        });
    }
    const existing = duty.data?.assignments.find((a)=>a.sowId === sowId);
    return /*#__PURE__*/ _jsx(AssignmentForm, {
        programId: programId,
        teamId: teamId,
        sowId: sowId,
        fixedDutyId: isNew ? '' : dutyParam,
        existing: existing
    }, `${sowId}:${dutyParam}:${existing?.id ?? ''}`);
}
function AssignmentForm({ programId, teamId, sowId, fixedDutyId, existing }) {
    const params = useCmDrawerParams();
    const lk = useCmLookups(programId);
    const [dutyId, setDutyId] = useState(fixedDutyId);
    const [handling, setHandling] = useState(existing?.handling || 'performed');
    const [routedTo, setRoutedTo] = useState(existing?.routedToWorkstreamId ?? '');
    const [notOwedReason, setNotOwedReason] = useState(existing?.notOwedReason || 'not_applicable');
    const [overrides, setOverrides] = useState(()=>seedOverrides(existing));
    const [locator, setLocator] = useState(existing?.sourceLocator ?? '');
    const [note, setNote] = useState(existing?.note ?? '');
    const [rules, setRules] = useState(()=>(existing?.teamRules ?? []).map((r)=>({
                id: r.id,
                draft: draftFromRule(r, true)
            })));
    const [receipt, setReceipt] = useState(null);
    const save = useSetDutyAssignment(programId, {
        errors: 'inline'
    });
    const close = ()=>params.close('assign');
    // The SOW's team, for the title and to keep it out of "routed to".
    const team = useWorkstream(programId, teamId);
    const sow = team.data?.sows.find((s)=>s.id === sowId);
    const duty = lk.dutyById.get(dutyId);
    const missingLocator = OVERRIDES.some((o)=>overrides[o.key].value.trim() !== '' && overrides[o.key].locator.trim() === '');
    const ruleProblem = rules.map((r)=>draftProblem(r.draft)).find(Boolean) ?? null;
    const problem = !dutyId ? 'Pick the deliverable.' : handling === 'performed' && ruleProblem ? ruleProblem : null;
    const onSave = ()=>{
        const performed = handling === 'performed';
        save.mutate({
            sowId,
            dutyId,
            handling,
            routedToWorkstreamId: handling === 'routed' ? routedTo : '',
            notOwedReason: handling === 'not_owed' ? notOwedReason : '',
            titleAsStated: performed ? overrides.titleAsStated.value.trim() : '',
            recipient: performed ? overrides.recipient.value.trim() : '',
            format: performed ? overrides.format.value.trim() : '',
            submissionMethod: performed ? overrides.submissionMethod.value.trim() : '',
            acceptanceCriteria: performed ? overrides.acceptanceCriteria.value.trim() : '',
            // The set is replaced whole: the sender override has no field here, so
            // the stored one (and its locator) is carried, never wiped.
            senderContactId: performed ? existing?.senderContactId ?? '' : '',
            overrideLocators: performed ? [
                ...OVERRIDES.filter((o)=>overrides[o.key].value.trim() !== '').map((o)=>({
                        field: o.field,
                        sourceLocator: overrides[o.key].locator.trim(),
                        quote: overrides[o.key].quote.trim()
                    })),
                ...existing?.senderContactId ? (existing.overrideLocators ?? []).filter((l)=>l.field === 'sender_contact_id').map((l)=>({
                        field: l.field,
                        sourceLocator: l.sourceLocator,
                        quote: l.quote
                    })) : []
            ] : [],
            sourceLocator: locator.trim(),
            note: note.trim(),
            teamRules: performed ? rules.map((r)=>({
                    id: r.id,
                    fields: {
                        ...fieldsFromDraft(r.draft),
                        gate: 'sow'
                    }
                })) : []
        }, {
            onSuccess: (r)=>setReceipt(mintReceipt(r.result?.mint))
        });
    };
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: duty ? duty.title : 'Handle an award deliverable',
        description: sow ? `${sow.title}${sow.version ? ` v${sow.version}` : ''}` : undefined,
        onClose: close,
        busy: save.isPending,
        testId: Testids.cmAssignment.drawer,
        footerProps: receipt ? {
            confirmLabel: 'Done',
            onConfirm: close,
            onCancel: close,
            cancelLabel: 'Close'
        } : {
            confirmLabel: 'Save',
            onConfirm: onSave,
            confirmDisabled: !!problem || save.isPending,
            loading: save.isPending,
            onCancel: close,
            confirmProps: {
                'data-testid': Testids.cmAssignment.save
            },
            leading: problem ? /*#__PURE__*/ _jsx(SpecraText, {
                size: "caption",
                tone: "muted",
                children: problem
            }) : undefined
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                receipt ? /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
                    tone: "success",
                    variant: "framed",
                    "data-testid": Testids.cmAssignment.receipt,
                    children: [
                        "Saved. ",
                        receipt,
                        "."
                    ]
                }) : null,
                fixedDutyId ? null : /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Deliverable",
                    required: true,
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: dutyId,
                        onChange: (e)=>setDutyId(e.target.value),
                        "data-testid": Testids.cmAssignment.dutyPicker,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Pick the award deliverable this SOW handles…"
                            }),
                            lk.duties.map((d)=>/*#__PURE__*/ _jsxs("option", {
                                    value: d.id,
                                    children: [
                                        d.title,
                                        d.pwsRef ? ` · PWS ${d.pwsRef}` : ''
                                    ]
                                }, d.id))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                    label: "Handling here",
                    value: handling,
                    options: [
                        {
                            value: 'performed',
                            label: 'Performed'
                        },
                        {
                            value: 'routed',
                            label: 'Routed'
                        },
                        {
                            value: 'not_owed',
                            label: 'Not owed'
                        }
                    ],
                    onValueChange: setHandling,
                    testId: Testids.cmAssignment.handling
                }),
                handling === 'routed' ? /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Routed to",
                    hint: "The team whose own exercise owes it instead.",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: routedTo,
                        onChange: (e)=>setRoutedTo(e.target.value),
                        "data-testid": Testids.cmAssignment.routedTo,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Base scope / contract-wide"
                            }),
                            lk.workstreams.filter((w)=>w.id !== teamId).map((w)=>/*#__PURE__*/ _jsx("option", {
                                    value: w.id,
                                    children: w.name
                                }, w.id))
                        ]
                    })
                }) : null,
                handling === 'not_owed' ? /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Why the SOW says it is not owed",
                    required: true,
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        value: notOwedReason,
                        onChange: (e)=>setNotOwedReason(e.target.value),
                        "data-testid": Testids.cmAssignment.notOwedReason,
                        children: Object.entries(NOT_OWED_REASON_LABEL).map(([v, l])=>/*#__PURE__*/ _jsx("option", {
                                value: v,
                                children: l
                            }, v))
                    })
                }) : null,
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Where the SOW says so",
                    hint: "e.g. “SOW App. A 3.3, p.12”",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: locator,
                        onChange: (e)=>setLocator(e.target.value)
                    })
                }),
                handling === 'performed' ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                    variant: "group",
                                    title: "Terms this SOW changes (blank = the award’s)"
                                }),
                                OVERRIDES.map((o)=>/*#__PURE__*/ _jsxs("div", {
                                        className: "grid grid-cols-1 sm:grid-cols-3 gap-2",
                                        "data-testid": Testids.cmAssignment.override,
                                        "data-field": o.field,
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                "aria-label": o.label,
                                                placeholder: o.label,
                                                value: overrides[o.key].value,
                                                onChange: (e)=>setOverrides({
                                                        ...overrides,
                                                        [o.key]: {
                                                            ...overrides[o.key],
                                                            value: e.target.value
                                                        }
                                                    })
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                "aria-label": `${o.label}: where`,
                                                placeholder: "Where (p.12)",
                                                value: overrides[o.key].locator,
                                                onChange: (e)=>setOverrides({
                                                        ...overrides,
                                                        [o.key]: {
                                                            ...overrides[o.key],
                                                            locator: e.target.value
                                                        }
                                                    }),
                                                "data-testid": Testids.cmAssignment.overrideLocator
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                "aria-label": `${o.label}: quote`,
                                                placeholder: "Quote",
                                                value: overrides[o.key].quote,
                                                onChange: (e)=>setOverrides({
                                                        ...overrides,
                                                        [o.key]: {
                                                            ...overrides[o.key],
                                                            quote: e.target.value
                                                        }
                                                    }),
                                                "data-testid": Testids.cmAssignment.overrideQuote
                                            })
                                        ]
                                    }, o.key)),
                                missingLocator ? /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    size: "caption",
                                    tone: "warning",
                                    "data-testid": Testids.cmAssignment.warning,
                                    children: "A changed term without “where” can’t be checked later. You can still save."
                                }) : null
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                                    variant: "group",
                                    title: "Team rules",
                                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "secondary",
                                        onClick: ()=>setRules([
                                                ...rules,
                                                {
                                                    id: '',
                                                    draft: draftFromRule(undefined, true)
                                                }
                                            ]),
                                        "data-testid": Testids.cmAssignment.addTeamRule,
                                        children: "Add a team rule"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    as: "p",
                                    size: "caption",
                                    tone: "muted",
                                    children: "A team rule replaces the award’s schedule for this team."
                                }),
                                rules.map((r, i)=>/*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-2 border-l-2 border-border pl-3",
                                        "data-testid": Testids.cmAssignment.teamRule,
                                        children: [
                                            /*#__PURE__*/ _jsx(RuleForm, {
                                                programId: programId,
                                                teamRule: true,
                                                value: r.draft,
                                                onChange: (d)=>setRules(rules.map((x, j)=>j === i ? {
                                                            ...x,
                                                            draft: d
                                                        } : x))
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                variant: "ghost",
                                                onClick: ()=>setRules(rules.filter((_, j)=>j !== i)),
                                                "data-testid": Testids.cmAssignment.removeTeamRule,
                                                children: "Remove this rule"
                                            })
                                        ]
                                    }, r.id || `new-${i}`))
                            ]
                        })
                    ]
                }) : null,
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Note",
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        rows: 2,
                        value: note,
                        onChange: (e)=>setNote(e.target.value)
                    })
                }),
                save.error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(save.error, 'Could not save')
                }) : null
            ]
        })
    });
}
